<template>
  <div class="summary">
    <WidgetNotification v-if="isNoData" :message="Lang.noData" />
    <div
      v-else
      class="summary__item-list"
      ref="draggable-list"
      :style="`grid-template-columns: repeat(${countColumn}, 1fr)`"
    >
      <div
        :class="['summary__item', { draggable: isEditMode }]"
        v-for="item in items"
        :key="item.name"
        grid-drag-ignore
      >
        <span class="summary__item-title ellipsis">{{ getTitle(item.name) }}</span>
        <span class="summary__item-value ellipsis">{{ getValue(item, useCompactFormat) }}</span>
        <span
          v-if="showTrends && item.badge && item.badge.isShown && !isEditMode"
          :class="['summary__item-label', areBadgesLoading ? trendColors.loading : item.badge.color]"
        >
          <v-icon v-if="item.badge.direction === trendDirections.up" size="14">mdi-arrow-top-right</v-icon>
          <v-icon v-else-if="item.badge.direction === trendDirections.down" size="14">mdi-arrow-bottom-right</v-icon>
          <v-icon v-else-if="item.badge.direction === trendDirections.same" size="14">mdi-equal</v-icon>

          <SpinnerLoader v-if="areBadgesLoading" :scale="0.4" transparent-background />
        </span>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-show="isEditMode && value.length > MIN_COUNT_ITEMS"
              class="summary__item-btn-remove action-icon"
              icon
              absolute
              small
              @click="removeMetric(item)"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon size="16">mdi-close</v-icon>
            </v-btn>
          </template>
          <span>{{ Lang.close }}</span>
        </v-tooltip>
      </div>
      <div v-if="isEditMode && itemsDialog.length" class="summary__item summary__item--new" @click="openDialog">
        <v-icon>mdi-plus-box-outline</v-icon>
        <span class="line-clamp ml-2">{{ Lang.addNewMetric }}</span>
      </div>
    </div>

    <v-dialog v-model="dialog" scrollable max-width="300px">
      <v-card>
        <v-card-title>
          {{ Lang.addNew }}
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ml-auto action-icon" icon @click="dialog = false" v-bind="attrs" v-on="on">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>{{ Lang.close }}</span>
          </v-tooltip>
        </v-card-title>
        <v-divider />
        <v-list>
          <v-list-item-group>
            <v-list-item v-for="item in itemsDialog" :key="item.value" @click="addMetric(item)">
              <v-list-item-title v-text="item.title"></v-list-item-title>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import Sortable from "sortablejs";
import Vue from "vue";

import SpinnerLoader from "@/components/loaders/SpinnerLoader.vue";
import WidgetNotification from "@/components/WidgetNotification.vue";
import { PLAN_MODULE } from "@/constants";
import Lang from "@/lang";
import exploreInputsDictionary from "@/settings/inputsDictionary";
import planInputsDictionary from "@/settings/planInputsDictionary";
import { SummaryItem, trendColors, trendDirections } from "@/settings/summary/summary";
import { InputDictionary } from "@/types/common";

export default Vue.extend({
  name: "Summary",

  props: {
    isEditMode: Boolean,
    isWidgetLoading: Boolean,
    areBadgesLoading: Boolean,
    items: Array as () => SummaryItem[],
    previousItems: Array as () => SummaryItem[],
    value: Array as () => string[],
    showTrends: Boolean,
    useCompactFormat: Boolean,
    module: {
      type: String,
      required: true,
    },
  },

  components: {
    SpinnerLoader,
    WidgetNotification,
  },

  data() {
    return {
      MIN_COUNT_ITEMS: 3,
      MAX_COUNT_ITEMS: 10,
      Lang,
      dialog: false,
      trendDirections,
      trendColors,
    };
  },

  mounted() {
    const element = this.$refs["draggable-list"] as HTMLElement;
    Sortable.create(element as HTMLElement, {
      disabled: !this.isEditMode,
      draggable: ".summary__item.draggable",
      onEnd: ({ newIndex, oldIndex }) => {
        this.$emit("onOrderChange", { newIndex, oldIndex });
      },
    });
  },

  computed: {
    itemsDialog(): InputDictionary[] {
      if (this.value?.length >= this.MAX_COUNT_ITEMS) {
        return [];
      }

      return this.inputsDictionary.summaryMetrics.filter((item) => {
        return !this.value.find((el: string) => {
          return item.value === el;
        });
      });
    },

    inputsDictionary(): Record<string, InputDictionary[]> {
      return (this.module === PLAN_MODULE ? planInputsDictionary : exploreInputsDictionary) as Record<
        string,
        InputDictionary[]
      >;
    },
    /**
     * Calculate the number of metric columns in View and Edit mode.
     *
     *  Example (calculation for MIN_COUNT_ITEMS = 4 and MAX_COUNT_ITEMS = 10):
     *  Length  10  9  8  7  6  5  4
     *  View     5  5  4  4  3  3  4
     *  Edit     5  5  5  4  4  3  5
     *  Note: length = MIN_COUNT_ITEMS is a special case, we show only one string in it.
     */
    countColumn(): number {
      let length = this.value?.length || 0;
      length = length + (this.isEditMode ? Number(length < this.MAX_COUNT_ITEMS) : 0);
      const minItems = this.MIN_COUNT_ITEMS + Number(this.isEditMode);

      return length > minItems ? Math.ceil(length / 2) : minItems;
    },

    isNoData(): boolean {
      return (
        this.items?.map((item) => item.value).filter((item) => item !== null && item !== 0).length === 0 &&
        this.previousItems?.map((item) => item.value).filter((item) => item !== null && item !== 0).length === 0 &&
        !this.isWidgetLoading
      );
    },
  },

  methods: {
    getTitle(name: string): string {
      const itemSetting = this.inputsDictionary.displayMetrics.find((el: InputDictionary) => {
        return el.value === name;
      });
      return itemSetting ? itemSetting.title : "";
    },

    getValue(item: SummaryItem, useCompactFormat = false): string | number | null {
      const itemSetting = this.inputsDictionary.displayMetrics.find((el: InputDictionary) => {
        return el.value === item.name;
      });

      if (!itemSetting?.formatter) return "";
      return useCompactFormat
        ? itemSetting.formatter(item.value, { notation: "compact" })
        : itemSetting.formatter(item.value);
    },

    openDialog(): void {
      this.dialog = true;
    },

    addMetric(metric: InputDictionary): void {
      const dataMetric = this.inputsDictionary.summaryMetrics.find((el) => {
        return el.value === metric.value;
      });

      if (dataMetric) {
        this.$emit("input", [...this.value, dataMetric.value], true);
      }

      this.dialog = false;
    },

    removeMetric(metric: SummaryItem): void {
      const metrics = this.value.filter((item: string) => {
        return item !== metric.name;
      });

      this.$emit("input", metrics, false);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme.scss";
@import "@/scss/custom_variables.scss";

$item-min-height: 80px;

.summary {
  height: calc(100% - 52px);
  &__item-list {
    display: grid;
    grid-gap: 8px;
    grid-template-rows: auto;
    height: 100%;
  }

  &__item {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: $item-min-height;
    padding: 16px 20px;
    background-color: var(--v-medium-base);
    border-radius: $border-radius;
    overflow: hidden;
    justify-content: center;

    &.draggable {
      cursor: move;
    }

    &.summary__item--new {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: row;
      min-height: $item-min-height;
      font-size: 16px;
      font-weight: 600;
      background-color: var(--v-medium-darken2);
      cursor: pointer;
    }
  }

  &__item-title {
    font-size: 14px;
  }

  &__item-value {
    font-size: 20px;
    font-weight: 600;
  }

  &__item-btn-remove {
    right: 4px;
    top: 4px;
  }

  &__item-label {
    position: absolute;
    top: 0;
    right: 0;
    width: 30px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom-left-radius: $border-radius;

    & .v-icon {
      color: var(--v-content-white-base) !important;
    }

    &.positive {
      background-color: var(--v-success-base);
    }

    &.negative {
      background-color: var(--v-error-base);
    }

    &.default {
      background-color: var(--v-accent-purple-base);
    }

    &.loading {
      background: rgba(0, 0, 0, 0);
      height: 30px;
    }
  }
}
</style>
